class VariantCardsV2 extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    
    this.variants = [];
    this.selectedVariantId = null;
    
    this.initializeVariants();
    this.render();
    this.setupEventListeners();
  }
  
  static get observedAttributes() {
    return ['data-variants', 'selected-variant-id'];
  }
  
  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== newValue) {
      switch (name) {
        case 'data-variants':
          this.initializeVariants();
          this.render();
          this.setupEventListeners();
          break;
        case 'selected-variant-id':
          this.selectedVariantId = newValue;
          this.updateSelectedVariant();
          break;
      }
    }
  }
  
  initializeVariants() {
    const variantsData = this.getAttribute('data-variants');
    if (!variantsData) return;
    
    try {
      this.variants = JSON.parse(variantsData);
      
      const urlParams = new URLSearchParams(window.location.search);
      const variantFromUrl = urlParams.get('variant');
      
      if (variantFromUrl) {
        const urlVariant = this.variants.find(v => v.id == variantFromUrl);
        if (urlVariant) {
          this.selectedVariantId = urlVariant.id;
          return;
        }
      }
      
      const firstAvailable = this.variants.find(v => v.available);
      if (firstAvailable) {
        this.selectedVariantId = firstAvailable.id;
      } else if (this.variants.length > 0) {
        this.selectedVariantId = this.variants[0].id;
      }
    } catch (e) {
      console.error('Error parsing variants data:', e);
    }
  }
  
  render() {
    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          --variant-text-color: var(--color-foreground, 51, 51, 51);
          --variant-border-color: var(--color-border, 224, 224, 224);
          --variant-active-bg: var(--color-button, 0, 0, 0);
          --variant-active-text: var(--color-button-text, 255, 255, 255);
        }
        
        .product-variants {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
          margin: 16px 0;
        }
        
        @media screen and (max-width: 749px) {
          .product-variants {
            grid-template-columns: 1fr;
          }
        }
        
        .variant-card {
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: flex-start;
          padding: 4px;
          border: 2px solid rgb(var(--variant-border-color));
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s ease;
          text-align: left;
          background-color: inherit;
        }
        
        .variant-card:hover:not(.disabled) {
          border-color: rgb(var(--variant-active-bg));
        }
        
        .variant-card.active {
          border-color: rgb(var(--variant-active-bg));
          background-color: rgb(var(--variant-active-bg));
        }
        
        .variant-card.active .variant-title {
          color: rgb(var(--variant-active-text));
        }
        
        .variant-card.disabled {
          border-color: rgba(var(--variant-text-color), 0.3);
          background-color: rgba(var(--variant-text-color), 0.05);
          cursor: not-allowed;
          opacity: 0.6;
        }
        
        .variant-card.disabled .variant-title {
          color: rgba(var(--variant-text-color), 0.5);
          text-decoration: line-through;
        }
        
        .variant-image {
          width: 45px;
          height: 45px;
          object-fit: cover;
          border-radius: 4px;
          margin-right: 12px;
          flex-shrink: 0;
        }
        
        .variant-image-placeholder {
          width: 60px;
          height: 45px;
          border-radius: 4px;
          margin-right: 12px;
          background-color: rgba(var(--variant-text-color), 0.1);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        
        .variant-image-placeholder svg {
          width: 30px;
          height: 30px;
          opacity: 0.5;
        }
        
        .variant-title {
          font-size: 14px;
          font-weight: 500;
          color: rgb(var(--variant-text-color));
          word-break: break-word;
          line-height: 1.3;
          flex: 1;
        }
        
        .variant-card.active .variant-title {
          font-weight: 600;
        }
      </style>
      <div class="product-variants">
        ${this.variants.map(variant => `
          <div class="variant-card${variant.id === this.selectedVariantId ? ' active' : ''}${!variant.available ? ' disabled' : ''}" 
               data-variant-id="${variant.id}"
               ${!variant.available ? 'aria-disabled="true"' : ''}>
            ${variant.image ? `
              <img class="variant-image" src="${variant.image}" alt="${variant.title}" loading="lazy">
            ` : `
              <div class="variant-image-placeholder">
                <svg viewBox="0 0 40 30" fill="none" stroke="currentColor" stroke-width="1.5">
                  <circle cx="8" cy="22" r="6"/>
                  <circle cx="32" cy="22" r="6"/>
                  <path d="M8 16 L14 4 L26 4 L22 16"/>
                </svg>
              </div>
            `}
            <span class="variant-title">${variant.title}</span>
          </div>
        `).join('')}
      </div>
    `;
  }
  
  setupEventListeners() {
    const variantCards = this.shadowRoot.querySelectorAll('.variant-card');
    
    variantCards.forEach(card => {
      card.addEventListener('click', () => {
        if (card.classList.contains('disabled')) {
          return;
        }
        const variantId = card.dataset.variantId;
        this.selectVariant(variantId);
      });
    });
  }
  
  selectVariant(variantId) {
    this.selectedVariantId = variantId;
    
    const variant = this.variants.find(v => v.id == variantId);
    const isAvailable = variant ? variant.available : false;
    
    const variantCards = this.shadowRoot.querySelectorAll('.variant-card');
    variantCards.forEach(card => {
      if (card.dataset.variantId === variantId) {
        card.classList.add('active');
      } else {
        card.classList.remove('active');
      }
    });
    
    this.updateURL(variantId);
    this.updateFormInputs(variantId);
    
    this.dispatchEvent(new CustomEvent('variant-change', { detail: { variantId, available: isAvailable } }));
  }
  
  updateURL(variantId) {
    const url = new URL(window.location.href);
    if (variantId) {
      url.searchParams.set('variant', variantId);
    } else {
      url.searchParams.delete('variant');
    }
    window.history.replaceState({}, '', url);
  }
  
  updateFormInputs(variantId) {
    const productForm = document.querySelector('.product-single__form');
    if (productForm) {
      const variantInputs = productForm.querySelectorAll('input[name="id"]');
      variantInputs.forEach(input => {
        input.value = variantId;
        input.disabled = false;
      });
    }
    
    const productSelect = document.getElementById('product-select');
    if (productSelect) {
      productSelect.value = variantId;
    }
  }
  
  updateSelectedVariant() {
    const variantCards = this.shadowRoot.querySelectorAll('.variant-card');
    variantCards.forEach(card => {
      if (card.dataset.variantId === this.selectedVariantId) {
        card.classList.add('active');
      } else {
        card.classList.remove('active');
      }
    });
  }
  
  getSelectedVariantId() {
    return this.selectedVariantId;
  }
  
  setSelectedVariant(variantId) {
    this.selectVariant(variantId);
  }
}

customElements.define('variant-cards-v2', VariantCardsV2);